import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Tab,
  Tabs,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  ButtonGroup,
} from '@mui/material';
import {
  Speed,
  Terrain,
  Timeline,
  TrendingUp,
  ExpandMore,
  Flag,
  Timer,
  DirectionsRun,
  PlayArrow,
  Stop,
  ArrowUpward,
  ArrowDownward,
  Refresh,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Utility function to format time ago (like Facebook posts)
const formatTimeAgo = (timestamp: string | Date): string => {
  const now = new Date();
  const lastUpdate = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);

  if (diffInSeconds < 5) return 'just now';
  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

// Get color based on data freshness
const getDataFreshnessColor = (timestamp: string | Date): string => {
  const now = new Date();
  const lastUpdate = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - lastUpdate.getTime()) / 1000);

  if (diffInSeconds < 30) return '#4caf50'; // Green - very fresh
  if (diffInSeconds < 60) return '#ff9800'; // Orange - getting old
  return '#f44336'; // Red - stale data
};

// Utility function to determine tracker status based on data freshness
const getTrackerStatus = (lastUpdate: string | Date, originalStatus?: string): 'active' | 'standby' | 'offline' => {
  const now = new Date();
  const lastUpdateTime = new Date(lastUpdate);
  const diffInMinutes = Math.floor((now.getTime() - lastUpdateTime.getTime()) / (1000 * 60));

  // If more than 30 minutes without data, consider offline
  if (diffInMinutes >= 30) {
    return 'offline';
  }

  // If more than 5 minutes without data, consider standby
  if (diffInMinutes >= 5) {
    return 'standby';
  }

  // Recent data, keep as active
  return 'active';
};

interface TrackerData {
  trackerId: string;
  title: string;
  customTitle?: string; // For backwards compatibility
  speed: number;
  elevation: number;
  currentRole: string;
  status: string;
  position: {
    lat: number;
    lng: number;
  };
  lastUpdate?: string; // ISO timestamp of last GPS data received
  batteryLevel?: number;
  signalStrength?: number;
  temperature?: number;
  odometer?: {
    status: 'running' | 'paused' | 'stopped';
    totalDistance: number;
    sessionDistance: number;
    sessionTime: number;
    pausedTime: number;
    averageSpeed: number;
  };
}

interface RaceProgress {
  primarySource?: string;
  fromStart?: {
    kmDone: number;
    displayKm: string;
    percentage: number;
  };
  toFinish?: {
    kmRemaining: number;
    displayKm: string;
    percentage: number;
  };
  totalDistance?: number;
  isValid?: boolean;
  gaps?: any[];
}

interface MetricsDashboardProps {
  trackers: TrackerData[];
  raceProgress?: RaceProgress;
  api?: any; // API hook for race control endpoints
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`metrics-tabpanel-${index}`}
      aria-labelledby={`metrics-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

// Helper functions to extract values from metrics objects
const getSpeedValue = (speed: any): number => {
  if (!speed) return 0;
  return typeof speed === 'object' ? speed.current || 0 : Number(speed) || 0;
};

const getElevationValue = (elevation: any): number => {
  if (!elevation) return 0;
  return typeof elevation === 'object' ? elevation.current || 0 : Number(elevation) || 0;
};

const MetricsDashboard: React.FC<MetricsDashboardProps> = ({ trackers, raceProgress, api }) => {
  const [tabValue, setTabValue] = useState(0);
  const [chartData, setChartData] = useState<Record<string, Array<{time: string, speed: number, elevation: number}>>>({});
  const lastUpdateTime = useRef<Record<string, Date>>({});
  const [raceState, setRaceState] = useState<'stopped' | 'running'>('stopped');
  const [loading, setLoading] = useState(false);
  const [orderedTrackers, setOrderedTrackers] = useState<TrackerData[]>([]);

  // Load saved order from localStorage on mount
  useEffect(() => {
    if (trackers.length > 0) {
      const savedOrder = localStorage.getItem('trackerOrder');

      if (savedOrder) {
        try {
          const savedTrackerIds = JSON.parse(savedOrder) as string[];

          // Reorder trackers based on saved order
          const reorderedTrackers: TrackerData[] = [];
          const trackersMap = new Map(trackers.map(t => [t.trackerId, t]));

          // First, add trackers in saved order
          savedTrackerIds.forEach(id => {
            const tracker = trackersMap.get(id);
            if (tracker) {
              reorderedTrackers.push(tracker);
              trackersMap.delete(id);
            }
          });

          // Then add any new trackers that weren't in saved order
          trackersMap.forEach(tracker => {
            reorderedTrackers.push(tracker);
          });

          setOrderedTrackers(reorderedTrackers);
        } catch (error) {
          console.error('Failed to parse saved tracker order:', error);
          setOrderedTrackers(trackers);
        }
      } else {
        setOrderedTrackers(trackers);
      }
    }
  }, [trackers]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Save tracker order to localStorage
  const saveOrderToLocalStorage = (trackersToSave: TrackerData[]) => {
    const trackerIds = trackersToSave.map(t => t.trackerId);
    localStorage.setItem('trackerOrder', JSON.stringify(trackerIds));
  };

  // Move tracker up in the list
  const moveTrackerUp = (index: number) => {
    if (index === 0) return; // Already at top
    const newOrder = [...orderedTrackers];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setOrderedTrackers(newOrder);
    saveOrderToLocalStorage(newOrder);
  };

  // Move tracker down in the list
  const moveTrackerDown = (index: number) => {
    if (index === orderedTrackers.length - 1) return; // Already at bottom
    const newOrder = [...orderedTrackers];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setOrderedTrackers(newOrder);
    saveOrderToLocalStorage(newOrder);
  };

  // Reset to original order
  const resetOrder = () => {
    setOrderedTrackers(trackers);
    localStorage.removeItem('trackerOrder');
  };

  // Initialize chart data for new trackers
  const initializeChartData = useCallback((trackerId: string) => {
    const data = [];
    const currentTime = new Date();
    for (let i = 30; i >= 0; i--) {
      const time = new Date(currentTime.getTime() - i * 1000); // 1-second intervals
      data.push({
        time: time.toLocaleTimeString('en-US', { hour12: false }),
        speed: 0,
        elevation: 0,
      });
    }
    return data;
  }, []);

  // Update chart data smoothly when trackers change
  useEffect(() => {
    const currentTime = new Date();
    const timeString = currentTime.toLocaleTimeString('en-US', { hour12: false });

    setChartData(prevData => {
      const newData = { ...prevData };

      trackers.forEach(tracker => {
        const trackerId = tracker.trackerId;

        // Skip if this tracker was updated less than 800ms ago (avoid too frequent updates)
        const lastUpdate = lastUpdateTime.current[trackerId];
        if (lastUpdate && (currentTime.getTime() - lastUpdate.getTime()) < 800) {
          return;
        }

        lastUpdateTime.current[trackerId] = currentTime;

        // Initialize if tracker doesn't exist
        if (!newData[trackerId]) {
          newData[trackerId] = initializeChartData(trackerId);
        }

        // Add new data point and remove oldest (sliding window)
        const newPoint = {
          time: timeString,
          speed: getSpeedValue(tracker.speed),
          elevation: getElevationValue(tracker.elevation),
        };

        // Create new array with added point and removed oldest
        newData[trackerId] = [
          ...newData[trackerId].slice(1), // Remove first (oldest) element
          newPoint // Add new element at the end
        ];
      });

      // Clean up data for trackers that no longer exist
      Object.keys(newData).forEach(trackerId => {
        if (!trackers.find(t => t.trackerId === trackerId)) {
          delete newData[trackerId];
          delete lastUpdateTime.current[trackerId];
        }
      });

      return newData;
    });
  }, [trackers, initializeChartData]);

  // Force re-render every second to update "time ago" displays
  const [, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  // Calculate real distance using Haversine formula
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c * 1000; // Distance in meters
  };

  // Generate gap data between trackers using real GPS positions
  const generateGapData = () => {
    const gapData = [];
    const trackersToUse = orderedTrackers.length > 0 ? orderedTrackers : trackers;
    for (let i = 0; i < trackersToUse.length - 1; i++) {
      const tracker1 = trackersToUse[i];
      const tracker2 = trackersToUse[i + 1];

      // Calculate real distance gap if both trackers have position data
      let distanceGap = 0;
      let timeGap = 0;

      if (tracker1.position && tracker2.position) {
        // Calculate distance using Haversine formula
        distanceGap = calculateDistance(
          tracker1.position.lat,
          tracker1.position.lng,
          tracker2.position.lat,
          tracker2.position.lng
        );

        // Estimate time gap based on average speed
        const speed1 = getSpeedValue(tracker1.speed);
        const speed2 = getSpeedValue(tracker2.speed);
        const avgSpeed = (speed1 + speed2) / 2;

        // Calculate time gap in seconds (distance in meters / speed in m/s)
        if (avgSpeed > 0) {
          timeGap = distanceGap / (avgSpeed / 3.6); // Convert km/h to m/s
        }
      }

      gapData.push({
        comparison: `${tracker1.title || tracker1.customTitle || tracker1.trackerId} → ${tracker2.title || tracker2.customTitle || tracker2.trackerId}`,
        gap: timeGap,
        distance: distanceGap,
      });
    }
    return gapData;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDistance = (meters: number) => {
    if (meters < 1000) return `${meters.toFixed(0)}m`;
    return `${(meters / 1000).toFixed(2)}km`;
  };

  // Race control functions
  const handleRaceStart = async () => {
    if (!api) return;

    setLoading(true);
    try {
      await api.post('/dashboard/race-progress/start');
      setRaceState('running');
    } catch (error) {
      console.error('Failed to start race:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRaceEnd = async () => {
    if (!api) return;

    setLoading(true);
    try {
      await api.post('/dashboard/race-progress/stop');
      setRaceState('stopped');
    } catch (error) {
      console.error('Failed to end race:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load initial race state
  useEffect(() => {
    if (!api) return;

    const loadRaceState = async () => {
      try {
        const response = await api.get('/dashboard/race-progress');
        setRaceState(response.data.isRunning ? 'running' : 'stopped');
      } catch (error) {
        console.error('Failed to load race state:', error);
      }
    };

    loadRaceState();
  }, [api]);

  return (
    <Box>
      {/* Race Progress Overview */}
      {raceProgress && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Race Progress
                {raceProgress.isValid === false && (
                  <Chip
                    label="Waiting for GPS data"
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>

              {/* Race Control Buttons - Hidden for now */}
              {false && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    label={raceState === 'running' ? 'Race Running' : 'Race Stopped'}
                    color={raceState === 'running' ? 'success' : 'default'}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <ButtonGroup size="small" disabled={loading}>
                    <Button
                      startIcon={<PlayArrow />}
                      onClick={handleRaceStart}
                      disabled={raceState === 'running' || loading}
                      color="success"
                    >
                      Start Race
                    </Button>
                    <Button
                      startIcon={<Stop />}
                      onClick={handleRaceEnd}
                      disabled={raceState === 'stopped' || loading}
                      color="error"
                    >
                      End Race
                    </Button>
                  </ButtonGroup>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Flag color="primary" />
                  <Typography variant="body2">
                    From Start: {raceProgress.fromStart?.displayKm || '0.0'} km
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <DirectionsRun color="primary" />
                  <Typography variant="body2">
                    To Finish: {raceProgress.toFinish?.displayKm || '0.0'} km
                  </Typography>
                </Stack>
              </Box>
            </Box>

            <LinearProgress
              variant="determinate"
              value={raceProgress.fromStart?.percentage || 0}
              sx={{ mb: 1, height: 8, borderRadius: 4 }}
            />

            <Typography variant="caption" color="text.secondary">
              {raceProgress.fromStart?.percentage ? Number(raceProgress.fromStart.percentage).toFixed(1) : 0}% Complete
              {raceProgress.totalDistance &&
                ` • Total: ${Number(raceProgress.totalDistance).toFixed(1)} km`}
              {raceProgress.primarySource &&
                ` • Source: ${raceProgress.primarySource}`}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Metrics Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="metrics tabs">
          <Tab label="Live Metrics" icon={<Speed />} />
          <Tab label="Speed Charts" icon={<TrendingUp />} />
          <Tab label="Gaps Analysis" icon={<Timeline />} />
        </Tabs>
      </Box>

      {/* Tab 1: Live Metrics */}
      <TabPanel value={tabValue} index={0}>
        {/* Reorder Controls */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            startIcon={<Refresh />}
            onClick={resetOrder}
            variant="outlined"
          >
            Reset Order
          </Button>
        </Box>

        <Stack spacing={2}>
          {orderedTrackers.map((tracker, index) => {
            // Compute actual status based on data freshness
            const computedStatus = tracker.lastUpdate
              ? getTrackerStatus(tracker.lastUpdate, tracker.status)
              : 'offline';

            const trackerWithStatus = {
              ...tracker,
              status: computedStatus
            };

            return (
              <Card key={tracker.trackerId} variant="outlined">
                <CardContent sx={{ p: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">
                        {tracker.title || tracker.customTitle || tracker.trackerId}
                        {tracker.currentRole && (tracker.title || tracker.customTitle) && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({tracker.currentRole})
                          </Typography>
                        )}
                      </Typography>
                      {raceProgress?.primarySource === tracker.trackerId && (
                        <Chip
                          label="Race Progress"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem', height: '20px' }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* Reorder Buttons */}
                      <ButtonGroup size="small" variant="outlined">
                        <Button
                          onClick={() => moveTrackerUp(index)}
                          disabled={index === 0}
                          sx={{ minWidth: '32px', p: 0.5 }}
                        >
                          <ArrowUpward fontSize="small" />
                        </Button>
                        <Button
                          onClick={() => moveTrackerDown(index)}
                          disabled={index === orderedTrackers.length - 1}
                          sx={{ minWidth: '32px', p: 0.5 }}
                        >
                          <ArrowDownward fontSize="small" />
                        </Button>
                      </ButtonGroup>

                      <Chip
                        label={trackerWithStatus.status}
                        size="small"
                        color={
                          trackerWithStatus.status === 'active' ? 'success' :
                          trackerWithStatus.status === 'standby' ? 'warning' : 'error'
                        }
                      />
                    {tracker.lastUpdate && (
                      <Chip
                        label={formatTimeAgo(tracker.lastUpdate)}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: getDataFreshnessColor(tracker.lastUpdate),
                          color: getDataFreshnessColor(tracker.lastUpdate),
                          fontSize: '0.7rem'
                        }}
                      />
                    )}
                  </Box>
                </Stack>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Speed fontSize="small" color="primary" />
                      <Box>
                        <Typography variant="h6">
                          {getSpeedValue(tracker.speed).toFixed(1)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          km/h
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Terrain fontSize="small" color="secondary" />
                      <Box>
                        <Typography variant="h6">
                          {getElevationValue(tracker.elevation).toFixed(0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          meters
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Timeline fontSize="small" color="info" />
                      <Box>
                        <Typography variant="h6">
                          {tracker.position ?
                            `${Number(tracker.position.lat).toFixed(4)}` : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          latitude
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h6">
                          {tracker.position ?
                            `${Number(tracker.position.lng).toFixed(4)}` : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          longitude
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Box>

                {/* Odometer Section - Hidden for now */}
                {false && tracker.odometer && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Odometer
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DirectionsRun fontSize="small" color="success" />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {tracker.odometer?.sessionDistance?.toFixed(2) || '0.00'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              session km
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Timer fontSize="small" color="info" />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {Math.floor((tracker.odometer?.sessionTime || 0) / 60)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              minutes
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Chip
                          label={tracker.odometer?.status || 'stopped'}
                          size="small"
                          color={
                            tracker.odometer?.status === 'running' ? 'success' :
                            tracker.odometer?.status === 'paused' ? 'warning' : 'default'
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          );
          })}
        </Stack>
      </TabPanel>

      {/* Tab 2: Speed Charts */}
      <TabPanel value={tabValue} index={1}>
        <Stack spacing={3}>
          {orderedTrackers.map((tracker) => { // Show all trackers
            const data = chartData[tracker.trackerId] || [];
            return (
              <Accordion key={tracker.trackerId}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">
                    {tracker.title || tracker.customTitle || tracker.currentRole} - Speed & Elevation
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ height: 200, width: '100%' }}>
                    <ResponsiveContainer>
                      <LineChart
                        data={data}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="time"
                          fontSize={10}
                          tick={{ fill: '#fff' }}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          yAxisId="speed"
                          fontSize={10}
                          tick={{ fill: '#fff' }}
                          domain={['dataMin - 5', 'dataMax + 5']}
                        />
                        <YAxis
                          yAxisId="elevation"
                          orientation="right"
                          fontSize={10}
                          tick={{ fill: '#fff' }}
                          domain={['dataMin - 10', 'dataMax + 10']}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #333'
                          }}
                        />
                        <Line
                          yAxisId="speed"
                          type="monotone"
                          dataKey="speed"
                          stroke="#2196f3"
                          strokeWidth={2}
                          dot={false}
                          name="Speed (km/h)"
                          isAnimationActive={false}
                        />
                        <Line
                          yAxisId="elevation"
                          type="monotone"
                          dataKey="elevation"
                          stroke="#ff9800"
                          strokeWidth={2}
                          dot={false}
                          name="Elevation (m)"
                          isAnimationActive={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </TabPanel>

      {/* Tab 3: Gaps Analysis */}
      <TabPanel value={tabValue} index={2}>
        <Stack spacing={2}>
          <Typography variant="subtitle2" gutterBottom>
            Time Gaps Between Trackers
          </Typography>

          {generateGapData().map((gap, index) => (
            <Card key={index} variant="outlined">
              <CardContent sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">
                    {gap.comparison}
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Chip
                      label={formatTime(gap.gap)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={formatDistance(gap.distance)}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}

          {orderedTrackers.length < 2 && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Need at least 2 active trackers to show gaps
            </Typography>
          )}
        </Stack>
      </TabPanel>
    </Box>
  );
};

export default MetricsDashboard;